<template>
  <Dialog
    header="Select Commodity"
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :modal="true"
    :closable="true"
    :style="{ width: '100vw', height: '100%', maxWidth: '100vw', maxHeight: '100%', margin: 0 }"
    :contentStyle="{ padding: '0', height: '100%' }"
    class="commodity-selection-dialog"
  >
    <div class="commodity-selection-container h-full flex flex-column">
      <!-- Header with search -->
      <div class="commodity-selection-header px-4 border-bottom-1 surface-border">
        <!-- Search Bar -->
        <div class="search-container">
          <IconField iconPosition="left">
            <InputIcon class="pi pi-search" />
            <InputText
              v-model="searchQuery"
              placeholder="Search by name, category, or description..."
              class="w-full"
            />
          </IconField>
        </div>
      </div>

      <!-- Content Area -->
      <div class="commodity-selection-content flex-grow-1 overflow-auto">
        <div v-if="loading" class="flex justify-content-center align-items-center h-full">
          <ProgressSpinner />
        </div>

        <div
          v-else-if="filteredCommodities.length === 0"
          class="flex justify-content-center align-items-center h-full"
        >
          <div class="text-center">
            <i class="pi pi-search text-6xl text-400 mb-3"></i>
            <h3 class="text-500">No commodities found</h3>
            <p class="text-400">Try adjusting your search terms</p>
          </div>
        </div>

        <div v-else class="commodity-grid p-4">
          <div
            v-for="commodity in filteredCommodities"
            :key="commodity.id"
            class="commodity-card"
            @click="selectCommodity(commodity)"
          >
            <Card
              class="h-full cursor-pointer hover:shadow-3 transition-all transition-duration-200"
            >
              <template #header>
                <div class="commodity-card-header p-3">
                  <div class="flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <h4 class="m-0 mb-1 text-sm font-semibold">{{ commodity.name }}</h4>
                      <Tag :value="commodity.category" severity="secondary" class="text-xs" />
                    </div>
                    <div class="text-right">
                      <div v-if="commodity.is_favorite" class="mt-1">
                        <i class="pi pi-star-fill text-yellow-500"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template #content>
                <div class="commodity-card-content p-3 pt-0">
                  <div v-if="commodity.description" class="text-xs text-600">
                    {{ commodity.description }}
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import type { CommodityType } from '@/types/CommodityType'
import { executeTopCommodities, type TopCommodityRow } from '@/reports/topCommodities'
import { useCommodityTypeService } from '@/services/commodityTypeService'

// Components
import Dialog from 'primevue/dialog'
import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'

// Props
const props = defineProps<{
  visible: boolean
}>()

// Emits
const emit = defineEmits<{
  'update:visible': [value: boolean]
  'commodity-selected': [commodity: CommodityType]
}>()

// Services
const commodityTypeService = useCommodityTypeService()

// State
const commodities = ref<CommodityType[]>([])
const topCommodities = ref<TopCommodityRow[]>([])
const searchQuery = ref('')
const loading = ref(true)

// Computed
const filteredCommodities = computed(() => {
  let filtered = commodities.value

  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(
      (commodity) =>
        commodity.name.toLowerCase().includes(query) ||
        commodity.category.toLowerCase().includes(query) ||
        (commodity.description && commodity.description.toLowerCase().includes(query)),
    )
  }

  // Sort by top commodities data (purchase count) first, then by name
  return filtered.sort((a, b) => {
    const statsA = topCommodities.value.find((stat) => stat.commodityId === a.id)
    const statsB = topCommodities.value.find((stat) => stat.commodityId === b.id)

    // First sort by transaction count (purchases)
    if (statsA && statsB) {
      const countDiff = statsB.transactionCount - statsA.transactionCount
      if (countDiff !== 0) return countDiff
    } else if (statsA && !statsB) {
      return -1
    } else if (!statsA && statsB) {
      return 1
    }

    // Then sort by name
    return a.name.localeCompare(b.name)
  })
})

// Methods

function selectCommodity(commodity: CommodityType) {
  emit('commodity-selected', commodity)
  emit('update:visible', false)
}

async function loadCommodities() {
  loading.value = true
  try {
    // Load commodity types using service
    commodities.value = await commodityTypeService.getActiveCommodityTypes()

    // Load top commodities data for sorting
    topCommodities.value = await executeTopCommodities('purchased')
  } catch (error) {
    console.error('Error loading commodities:', error)
  } finally {
    loading.value = false
  }
}

// Watch for dialog visibility to load data
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      loadCommodities()
    }
  },
)

// Load data on mount
onMounted(() => {
  if (props.visible) {
    loadCommodities()
  }
})
</script>

<style scoped>
.commodity-selection-dialog :deep(.p-dialog) {
  margin: 0;
  border-radius: 0;
  height: 100vh;
  height: 100dvh; /* Use dynamic viewport height for mobile */
}

.commodity-selection-dialog :deep(.p-dialog-content) {
  border-radius: 0;
  height: 100%;
  overflow: hidden;
}

.commodity-selection-container {
  background: var(--surface-ground);
}

.commodity-selection-header {
  background: var(--surface-card);
  border-bottom: 1px solid var(--surface-border);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.search-container {
  max-width: 500px;
}

.commodity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  max-width: 1400px;
  margin: 0 auto;
}

.commodity-card {
  min-height: 120px;
}

.commodity-card :deep(.p-card) {
  height: 100%;
  border: 1px solid var(--surface-border);
  transition: all 0.2s ease;
}

.commodity-card :deep(.p-card):hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.commodity-card-header {
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
  padding: 0.75rem !important;
}

.commodity-card-content {
  background: var(--surface-card);
  padding: 0.5rem 0.75rem !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .commodity-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .commodity-selection-header {
    padding: 1rem;
    /* Ensure header doesn't go behind address bar on mobile */
    padding-top: max(1rem, env(safe-area-inset-top));
  }

  .commodity-grid {
    padding: 1rem;
  }

  /* Mobile-specific viewport handling */
  .commodity-selection-dialog :deep(.p-dialog) {
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile */
    height: 100svh; /* Small viewport height for mobile */
  }
}

@media (min-width: 1200px) {
  .commodity-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}
</style>
